/** Escapa caracteres HTML para evitar XSS. */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function isTableRow(line: string): boolean {
  return /^\|.+\|$/.test(line.trim());
}

function parseTableCells(row: string): string[] {
  return row
    .trim()
    .split('|')
    .slice(1, -1)
    .map((cell) => cell.trim());
}

function isSeparatorRow(cells: string[]): boolean {
  return cells.length > 0 && cells.every((cell) => /^:?-+:?$/.test(cell));
}

/**
 * Convierte un subconjunto de Markdown a HTML.
 * Cubre h1-h3, listas, tablas GFM, párrafos, negrita e itálica.
 */
export function markdownToHtml(markdown: string): string {
  const lines = markdown.split('\n');
  const out: string[] = [];
  let inList = false;
  let tableRows: string[] = [];

  const inlineFormat = (text: string): string => {
    const links: string[] = [];
    const tokenized = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, label: string, href: string) => {
      const token = `@@LINK${links.length}@@`;
      links.push(`<a href="${escapeHtml(href)}">${escapeHtml(label)}</a>`);
      return token;
    });

    let result = escapeHtml(tokenized)
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/_(.+?)_/g, '<em>$1</em>');

    links.forEach((link, i) => {
      result = result.replace(`@@LINK${i}@@`, link);
    });

    return result;
  };

  const renderTable = (rows: string[]): string => {
    if (rows.length === 0) return '';

    const parsed = rows.map(parseTableCells).filter((cells) => cells.length > 0);
    if (parsed.length === 0) return '';

    const [headerCells, ...rest] = parsed;
    const bodyStart = rest.length > 0 && isSeparatorRow(rest[0]) ? 1 : 0;
    const bodyRows = rest.slice(bodyStart);

    const header = headerCells.map((cell) => `<th>${inlineFormat(cell)}</th>`).join('');
    const body = bodyRows
      .map(
        (cells) =>
          `<tr>${cells.map((cell) => `<td>${inlineFormat(cell)}</td>`).join('')}</tr>`,
      )
      .join('');

    return `<div class="md-table-wrap"><table><thead><tr>${header}</tr></thead><tbody>${body}</tbody></table></div>`;
  };

  const flushTable = () => {
    if (tableRows.length > 0) {
      out.push(renderTable(tableRows));
      tableRows = [];
    }
  };

  const closeList = () => {
    if (inList) {
      out.push('</ul>');
      inList = false;
    }
  };

  const closeBlocks = () => {
    closeList();
    flushTable();
  };

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (isTableRow(line)) {
      closeList();
      tableRows.push(line.trim());
    } else if (/^### /.test(line)) {
      closeBlocks();
      out.push(`<h3>${inlineFormat(line.slice(4))}</h3>`);
    } else if (/^## /.test(line)) {
      closeBlocks();
      out.push(`<h2>${inlineFormat(line.slice(3))}</h2>`);
    } else if (/^# /.test(line)) {
      closeBlocks();
      out.push(`<h1>${inlineFormat(line.slice(2))}</h1>`);
    } else if (/^[-*] /.test(line)) {
      flushTable();
      if (!inList) {
        out.push('<ul>');
        inList = true;
      }
      out.push(`<li>${inlineFormat(line.slice(2))}</li>`);
    } else if (line.trim() === '') {
      closeBlocks();
      out.push('');
    } else {
      closeBlocks();
      out.push(`<p>${inlineFormat(line)}</p>`);
    }
  }

  closeBlocks();
  return out.join('\n');
}
